/**
 * Billing country drives money/number/date *formatting* and (later) the
 * Smart Bill Import country pack. It is independent of the app language:
 * English UI + France billing is a normal setup.
 */
export interface Region {
  /** ISO 3166-1 alpha-2 */
  code: string;
  currency: string;
  /** BCP 47 locale used for numbers, currency and numeric dates. */
  formatLocale: string;
}

export const REGIONS: readonly Region[] = [
  { code: 'US', currency: 'USD', formatLocale: 'en-US' },
  { code: 'GB', currency: 'GBP', formatLocale: 'en-GB' },
  { code: 'TR', currency: 'TRY', formatLocale: 'tr-TR' },
  { code: 'FR', currency: 'EUR', formatLocale: 'fr-FR' },
  { code: 'DE', currency: 'EUR', formatLocale: 'de-DE' },
  { code: 'ES', currency: 'EUR', formatLocale: 'es-ES' },
  { code: 'IT', currency: 'EUR', formatLocale: 'it-IT' },
  { code: 'NL', currency: 'EUR', formatLocale: 'nl-NL' },
  { code: 'BE', currency: 'EUR', formatLocale: 'nl-BE' },
  { code: 'AT', currency: 'EUR', formatLocale: 'de-AT' },
  { code: 'IE', currency: 'EUR', formatLocale: 'en-IE' },
  { code: 'PT', currency: 'EUR', formatLocale: 'pt-PT' },
  { code: 'CH', currency: 'CHF', formatLocale: 'de-CH' },
  { code: 'CA', currency: 'CAD', formatLocale: 'en-CA' },
  { code: 'AU', currency: 'AUD', formatLocale: 'en-AU' },
  { code: 'NZ', currency: 'NZD', formatLocale: 'en-NZ' },
  { code: 'FI', currency: 'EUR', formatLocale: 'fi-FI' },
  { code: 'GR', currency: 'EUR', formatLocale: 'el-GR' },
  { code: 'SE', currency: 'SEK', formatLocale: 'sv-SE' },
  { code: 'NO', currency: 'NOK', formatLocale: 'nb-NO' },
  { code: 'DK', currency: 'DKK', formatLocale: 'da-DK' },
  { code: 'PL', currency: 'PLN', formatLocale: 'pl-PL' },
  { code: 'CZ', currency: 'CZK', formatLocale: 'cs-CZ' },
  { code: 'JP', currency: 'JPY', formatLocale: 'ja-JP' },
];

export const DEFAULT_REGION: Region = REGIONS[0]!;

/** Currencies offered in onboarding: every region's currency, most common first. */
export const CURRENCIES: readonly string[] = (() => {
  const first = ['USD', 'EUR', 'GBP'];
  const rest = [...new Set(REGIONS.map((r) => r.currency))].filter((c) => !first.includes(c)).sort();
  return [...first, ...rest];
})();

export function currencyName(code: string, appLanguage: string): string {
  return new Intl.DisplayNames([appLanguage], { type: 'currency' }).of(code) ?? code;
}

export function currencySymbol(code: string, formatLocale: string): string {
  // narrowSymbol: "$" not "$US"; the ISO code shown beside it tells dollars apart.
  return new Intl.NumberFormat(formatLocale, { style: 'currency', currency: code, currencyDisplay: 'narrowSymbol' }).formatToParts(0).find((p) => p.type === 'currency')?.value ?? code;
}

/**
 * Best guess for the country picker's starting point from the device
 * locale (no GPS, no network): "fr-FR" -> FR. The user always confirms.
 */
export function guessRegion(locales: readonly string[] = globalThis.navigator?.languages ?? []): Region | undefined {
  for (const tag of locales) {
    const region = tag.split('-')[1]?.toUpperCase();
    const found = region && findRegion(region);
    if (found) return found;
  }
  return undefined;
}

export function findRegion(code: string): Region | undefined {
  return REGIONS.find((r) => r.code === code);
}

/** Country name in the user's app language, e.g. ("FR", "tr") -> "Fransa". */
export function regionName(code: string, appLanguage: string): string {
  return new Intl.DisplayNames([appLanguage], { type: 'region' }).of(code) ?? code;
}
